(function () {
  const POPUP_SELECTOR = '[data-hazz-pack-popup]';
  const STORAGE_PREFIX = 'hazzPackRewards:';

  function q(root, selector) {
    return root.querySelector(selector);
  }

  function daysToMs(days) {
    return Number(days || 14) * 24 * 60 * 60 * 1000;
  }

  function canShow(popup) {
    try {
      if (localStorage.getItem(`${STORAGE_PREFIX}claimed`)) return false;
      const dismissedAt = Number(localStorage.getItem(`${STORAGE_PREFIX}dismissedAt`) || 0);
      const frequencyDays = Number(popup.dataset.frequencyDays || 14);
      return !dismissedAt || Date.now() - dismissedAt > daysToMs(frequencyDays);
    } catch {
      return true;
    }
  }

  function setDismissed() {
    try { localStorage.setItem(`${STORAGE_PREFIX}dismissedAt`, String(Date.now())); } catch {}
  }

  function setClaimed(code) {
    try {
      localStorage.setItem(`${STORAGE_PREFIX}claimed`, JSON.stringify({
        claimedAt: Date.now(),
        code: code || null,
      }));
    } catch {}
  }

  function showStep(popup, stepName) {
    popup.querySelectorAll('[data-hazz-pack-step]').forEach((step) => {
      step.hidden = step.dataset.hazzPackStep !== stepName;
    });
  }

  async function applyDiscount(code) {
    if (!code) return false;
    try {
      await fetch(`/discount/${encodeURIComponent(code)}`, {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'follow',
      });
      return true;
    } catch {
      return false;
    }
  }

  async function copyText(text) {
    if (!text) return false;
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }

  function submitNewsletterIfAllowed(popup, email, acceptsMarketing) {
    if (!acceptsMarketing || !email) return;

    const nativeForm = q(popup, '.hazz-pack-native-form');
    const nativeEmail = q(popup, '[data-hazz-pack-native-email]');
    const nativeFrame = q(popup, '.hazz-pack-native-frame');

    if (!nativeForm || !nativeEmail || !nativeFrame) return;

    try {
      nativeEmail.value = email;
      nativeForm.setAttribute('target', nativeFrame.getAttribute('name'));
      nativeForm.submit();
    } catch {
      // Email capture is non-blocking. Reward reveal should continue even if this fails.
    }
  }

  function openPopup(popup) {
    popup.hidden = false;
    document.documentElement.classList.add('hazz-pack-popup-open');
    const input = q(popup, 'input[type="email"]');
    setTimeout(() => input?.focus(), 120);
  }

  function closePopup(popup) {
    popup.hidden = true;
    popup.classList.remove('is-opening');
    document.documentElement.classList.remove('hazz-pack-popup-open');
    setDismissed();
  }

  function restartOpeningAnimation(popup) {
    popup.classList.remove('is-opening');
    void popup.offsetWidth;
    popup.classList.add('is-opening');
  }

  function setRewardContent(popup, code) {
    const rarity = popup.dataset.rewardRarity || 'Guaranteed Reward';
    const headline = popup.dataset.rewardHeadline || 'Your reward is ready';
    const copy = popup.dataset.rewardCopy || 'Your guaranteed reward has been added to checkout.';
    const expiry = popup.dataset.rewardExpiry || 'One use per customer. Minimum spend applies.';

    q(popup, '[data-hazz-pack-rarity]').textContent = rarity;
    q(popup, '[data-hazz-pack-headline]').textContent = headline;
    q(popup, '[data-hazz-pack-copy]').textContent = copy;
    q(popup, '[data-hazz-pack-code]').textContent = code || '';
    q(popup, '[data-hazz-pack-expiry]').textContent = expiry;
  }

  function bindPopup(popup) {
    if (popup.dataset.hazzPackBound === 'true') return;
    popup.dataset.hazzPackBound = 'true';

    const form = q(popup, '[data-hazz-pack-form]');
    const submit = q(popup, '[data-hazz-pack-submit]');
    const error = q(popup, '[data-hazz-pack-error]');
    const applyButton = q(popup, '[data-hazz-pack-apply]');
    const copyButton = q(popup, '[data-hazz-pack-copy-code]');

    popup.querySelectorAll('[data-hazz-pack-close]').forEach((button) => {
      button.addEventListener('click', () => closePopup(popup));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !popup.hidden) closePopup(popup);
    });

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();

      if (error) {
        error.hidden = true;
        error.textContent = '';
      }

      const formData = new FormData(form);
      const email = String(formData.get('email') || '').trim();
      const acceptsMarketing = formData.get('acceptsMarketing') === 'on';
      const code = String(popup.dataset.discountCode || '').trim();

      if (!email) return;

      if (!code) {
        if (error) {
          error.textContent = 'Please add a discount code in the Hazz Pack Popup section settings.';
          error.hidden = false;
        }
        return;
      }

      submit.disabled = true;
      submitNewsletterIfAllowed(popup, email, acceptsMarketing);
      restartOpeningAnimation(popup);
      showStep(popup, 'opening');

      try {
        await new Promise((resolve) => setTimeout(resolve, 3350));

        setRewardContent(popup, code);
        await applyDiscount(code);

        const redirectUrl = popup.dataset.redirectUrl || '/collections/all';
        const encodedCode = encodeURIComponent(code);
        const encodedRedirect = encodeURIComponent(redirectUrl);
        applyButton.href = `/discount/${encodedCode}?redirect=${encodedRedirect}`;
        applyButton.textContent = 'Apply Reward & Shop';

        copyButton.onclick = async () => {
          const copied = await copyText(code);
          copyButton.textContent = copied ? 'Copied' : 'Copy manually';
          setTimeout(() => { copyButton.textContent = 'Copy Code'; }, 1400);
        };

        setClaimed(code);
        popup.classList.remove('is-opening');
        showStep(popup, 'reward');
      } catch (err) {
        popup.classList.remove('is-opening');
        showStep(popup, 'intro');
        if (error) {
          error.textContent = err.message || 'Could not reveal reward.';
          error.hidden = false;
        }
      } finally {
        submit.disabled = false;
      }
    });

    const delaySeconds = Number(popup.dataset.delaySeconds || 6);
    if (canShow(popup)) {
      setTimeout(() => openPopup(popup), Math.max(0, delaySeconds) * 1000);
    }

    if (popup.dataset.openOnExit === 'true') {
      let exitShown = false;
      document.addEventListener('mouseout', (event) => {
        if (exitShown || !canShow(popup) || !popup.hidden) return;
        if (event.clientY <= 0) {
          exitShown = true;
          openPopup(popup);
        }
      });
    }
  }

  function init() {
    document.querySelectorAll(POPUP_SELECTOR).forEach(bindPopup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
